import React, { useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { X, Loader2, Sparkles, Check, Send, Copy, Pencil } from 'lucide-react';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

export const DraftReply = ({ proposalId, clientName, clientPhone, hasDm, onClose, onDone }) => {
  const [draft, setDraft] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState('');
  const [usedAi, setUsedAi] = useState(false);

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const res = await api.post(`/api/admin/leads/${proposalId}/draft-reply`, {});
      setDraft(res.draft);
      setUsedAi(true);
    } catch (e) {
      toast.error('Draft failed — try again');
    }
    setAiBusy(false);
  };

  const markResponded = async (silent) => {
    try {
      await api.post(`/api/admin/leads/${proposalId}/mark-responded`, {});
      if (!silent) toast.success('Marked responded');
      onDone && onDone();
    } catch (e) { toast.error('Failed'); }
  };

  const sendDm = async () => {
    setBusy('dm');
    try {
      await api.post('/api/admin/inbox/reply', { proposal_id: proposalId, message: draft });
      toast.success('DM sent');
      onDone && onDone();
    } catch (e) { toast.error(e.message || 'DM send failed'); }
    setBusy('');
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="draft-reply-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0A0A0A] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5" style={FONT_B}>
              <Pencil className="w-3.5 h-3.5" /> {usedAi ? 'OZ drafted · edit anything' : 'Write reply'}
            </p>
            <h3 className="text-white text-xl font-black tracking-tighter mt-1" style={FONT_H}>{clientName}</h3>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-zinc-400 active:scale-95 transition-transform" data-testid="draft-reply-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={6}
          placeholder="Type your message… or tap Draft with OZ to have AI compose one for you."
          className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3.5 text-sm text-white leading-relaxed focus:outline-none focus:border-blue-500/40 resize-none placeholder:text-zinc-600"
          style={FONT_B}
          data-testid="draft-reply-text"
          autoFocus
        />
        <div className="flex items-center justify-between mt-2 mb-4">
          <button
            onClick={generateAI}
            disabled={aiBusy}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-blue-500/15 text-blue-400 text-xs font-bold active:scale-95 disabled:opacity-40 transition-transform"
            style={FONT_B}
            data-testid="draft-reply-ai"
          >
            {aiBusy
              ? <><Loader2 className="w-3 h-3 animate-spin" /> OZ is writing…</>
              : <><Sparkles className="w-3 h-3" /> {draft ? 'Rewrite with OZ' : 'Draft with OZ'}</>}
          </button>
          <span className="text-zinc-700 text-[11px] tabular-nums" style={FONT_B}>{draft.length} chars</span>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {clientPhone && (
            <a
              href={draft ? `sms:${clientPhone}?&body=${encodeURIComponent(draft)}` : `sms:${clientPhone}`}
              onClick={() => { if (draft.trim()) markResponded(true); }}
              className={`flex items-center gap-2 px-7 py-3 rounded-full text-sm font-bold active:scale-95 transition-transform ${draft.trim() ? 'bg-white text-black' : 'bg-white/10 text-zinc-500 pointer-events-none'}`}
              style={FONT_B}
              data-testid="draft-reply-send-text"
            >
              <Send className="w-4 h-4" /> Send text
            </a>
          )}
          {hasDm && (
            <button onClick={sendDm} disabled={busy === 'dm' || !draft.trim()}
                    className="flex items-center gap-2 px-6 py-3 rounded-full bg-fuchsia-500/15 text-fuchsia-400 text-sm font-bold active:scale-95 disabled:opacity-40 transition-transform" style={FONT_B} data-testid="draft-reply-send-dm">
              {busy === 'dm' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send DM
            </button>
          )}
          <button onClick={copyDraft} disabled={!draft.trim()}
                  className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm font-bold active:scale-95 disabled:opacity-40 transition-transform" style={FONT_B} data-testid="draft-reply-copy">
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>
        <button onClick={() => markResponded(false)}
                className="flex items-center gap-1.5 text-zinc-600 text-xs font-bold mt-5 hover:text-zinc-400 transition-colors" style={FONT_B} data-testid="draft-reply-mark-responded">
          <Check className="w-3.5 h-3.5" /> Mark handled without sending
        </button>
      </div>
    </div>
  );
};
